import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Flag, Trophy } from 'lucide-react';

const About = ({ teamData }) => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = React.useState(false);
  const [counters, setCounters] = React.useState({
    piloti: 0,
    categorie: 0,
    gare: 0,
    professionalita: 0
  });
  const statsRef = React.useRef(null);

  const values = [
    {
      icon: Trophy,
      title: "Campionati",
      description: "Competizioni Nazionali, Internazionali ed Europee e Mondiali",
      link: "/campionati"
    },
    {
      icon: Flag,
      title: "Gare",
      description: "Race Report di ogni nostra gara",
      link: "/gare"
    },
    {
      icon: Calendar,
      title: "Calendario",
      description: "La nostra stagione 2026 riassunta in un calendario",
      link: "/calendario"
    }
  ];

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (statsRef.current) {
      observer.observe(statsRef.current);
    }

    return () => {
      if (statsRef.current) {
        observer.unobserve(statsRef.current);
      }
    };
  }, [isVisible]);

  React.useEffect(() => {
    if (isVisible) {
      const duration = 1200;
      const steps = 60;
      const stepDuration = duration / steps;

      const targets = {
        piloti: 2,
        categorie: 2,
        gare: 20,
        professionalita: 100
      };

      let currentStep = 0;

      const interval = setInterval(() => {
        currentStep++;
        const progress = currentStep / steps;

        setCounters({
          piloti: Math.floor(targets.piloti * progress),
          categorie: Math.floor(targets.categorie * progress),
          gare: Math.floor(targets.gare * progress),
          professionalita: Math.floor(targets.professionalita * progress)
        });

        if (currentStep >= steps) {
          setCounters(targets);
          clearInterval(interval);
        }
      }, stepDuration);

      return () => clearInterval(interval);
    }
  }, [isVisible]);

  return (
    <section className="py-20 bg-gradient-to-b from-black via-gray-900 to-black relative overflow-hidden">
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-96 h-96 bg-cyan-500 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-yellow-500 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-5xl md:text-6xl font-black text-white mb-4">
            Chi <span className="text-cyan-400">Siamo</span>
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-cyan-500 to-yellow-400 mx-auto"></div>
        </div>

        <div className="max-w-4xl mx-auto mb-16">
          <p className="text-xl md:text-2xl text-gray-300 text-center leading-relaxed mb-6">
            Il Team Seven Motorsport, con sede a Rieti, nasce come squadra dedicata allo sviluppo di giovani talenti del motocross.
          </p>
          <p className="text-xl md:text-2xl text-gray-300 text-center leading-relaxed mb-8">
            Nel corso degli anni ha costruito un percorso solido, raggiungendo un traguardo importante: per la stagione 2026 è ufficialmente riconosciuto come Team Factory TM Support, ottenendo il supporto diretto della casa madre e l'accesso a materiale racing esclusivo.
          </p>
          <p className="text-lg md:text-xl text-gray-400 text-center leading-relaxed">
            Si tratta di un programma sportivo di alto profilo, con ampia visibilità mediatica e una presenza costante nelle principali piste europee.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {values.map((value, index) => (
            <button
              key={index}
              onClick={() => navigate(value.link)}
              className="group bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm p-8 rounded-2xl border border-gray-700/50 hover:border-cyan-500/50 transition-all duration-300 hover:transform hover:-translate-y-2 text-left"
            >
              <div className="bg-gradient-to-br from-cyan-500 to-cyan-600 w-16 h-16 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-cyan-500/50">
                <value.icon className="w-8 h-8 text-black" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">{value.title}</h3>
              <p className="text-gray-400 leading-relaxed">{value.description}</p>
            </button>
          ))}
        </div>

        <div ref={statsRef} className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-8 max-w-5xl mx-auto">
          <div className="text-center">
            <div className="text-5xl md:text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-cyan-600 mb-2">
              {counters.piloti}
            </div>
            <div className="text-gray-400 font-semibold uppercase tracking-wide">Piloti</div>
          </div>
          <div className="text-center">
            <div className="text-5xl md:text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-cyan-600 mb-2">
              {counters.categorie}
            </div>
            <div className="text-gray-400 font-semibold uppercase tracking-wide">Categorie</div>
          </div>
          <div className="text-center">
            <div className="text-5xl md:text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-cyan-600 mb-2">
              {counters.gare}+
            </div>
            <div className="text-gray-400 font-semibold uppercase tracking-wide">Gare 2026</div>
          </div>
          <div className="text-center">
            <div className="text-5xl md:text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-cyan-600 mb-2">
              {counters.professionalita}%
            </div>
            <div className="text-gray-400 font-semibold uppercase tracking-wide">Professionalità</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
